import React, { useEffect, useState, useCallback } from 'react';
import { Mic, Headphones, CheckCircle, AlertTriangle, XCircle, RefreshCw, ArrowRight, ChevronRight } from 'lucide-react';
import { detectAudioDevices, DetectedDevice } from '../services/DeviceService';

type CheckState = 'checking' | 'detected' | 'not_detected' | 'permission_denied';

interface HeadsetCheckModalProps {
  recordingType: 'audio' | 'video';
  onProceed: (headsetDetected: boolean, devices: DetectedDevice[], overrideDevice?: DetectedDevice) => void;
}

export const HeadsetCheckModal: React.FC<HeadsetCheckModalProps> = ({
  recordingType,
  onProceed,
}) => {
  const [checkState, setCheckState] = useState<CheckState>('checking');
  const [devices, setDevices] = useState<DetectedDevice[]>([]);
  const [isRechecking, setIsRechecking] = useState(false);
  const [selectingOverride, setSelectingOverride] = useState(false);

  const runCheck = useCallback(async () => {
    setCheckState('checking');
    setSelectingOverride(false);
    const result = await detectAudioDevices();

    if (!result.permissionGranted) {
      setCheckState('permission_denied');
      return;
    }

    setDevices(result.devices);
    setCheckState(result.hasExternalInput ? 'detected' : 'not_detected');
  }, []);

  useEffect(() => {
    runCheck();
  }, [runCheck]);

  useEffect(() => {
    const handleDeviceChange = () => {
      runCheck();
    };
    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
    };
  }, [runCheck]);

  const handleRecheck = async () => {
    setIsRechecking(true);
    await runCheck();
    setIsRechecking(false);
  };

  const handleProceed = () => {
    onProceed(checkState === 'detected', devices);
  };

  const handleContinueAnyway = () => {
    onProceed(false, devices);
  };

  const handleSelectOverride = (device: DetectedDevice) => {
    onProceed(true, devices, device);
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[60]">
      <div className="bg-kod-purple-800 border border-kod-purple-600 rounded-2xl w-full max-w-md mx-4 overflow-hidden shadow-2xl">

        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-kod-purple-600/50">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-kod-purple-700">
              <Headphones size={20} className="text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold leading-tight">Headset Check</h2>
              <p className="text-white/50 text-xs mt-0.5">
                You need an external headset to be scored properly
              </p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-6">

          {/* CHECKING */}
          {checkState === 'checking' && (
            <div className="flex flex-col items-center py-6 gap-4">
              <div className="relative">
                <div className="w-16 h-16 rounded-full bg-kod-purple-700/50 flex items-center justify-center">
                  <Mic size={28} className="text-white/60" />
                </div>
                <div className="absolute inset-0 rounded-full border-2 border-kod-accent/40 border-t-kod-accent animate-spin" />
              </div>
              <p className="text-white/70 text-sm">Checking your audio devices...</p>
            </div>
          )}

          {/* DETECTED */}
          {checkState === 'detected' && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 rounded-xl bg-green-500/10 border border-green-500/20">
                <CheckCircle size={20} className="text-green-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-green-300 font-medium text-sm">External headset detected</p>
                  <p className="text-white/60 text-xs mt-1">
                    You're all set — your recording will be scored accurately.
                  </p>
                </div>
              </div>

              {devices.length > 0 && (
                <div>
                  <p className="text-white/50 text-xs font-medium uppercase tracking-wider mb-2">
                    Detected Devices
                  </p>
                  <div className="space-y-1.5">
                    {devices.map(d => (
                      <div
                        key={d.deviceId}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-kod-purple-700/50 text-sm"
                      >
                        <Mic size={13} className="text-white/40 flex-shrink-0" />
                        <span className="text-white/80 truncate">{d.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <button
                onClick={handleProceed}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-green-600 hover:bg-green-500 text-white font-semibold transition-colors"
              >
                Start Recording
                <ArrowRight size={16} />
              </button>
            </div>
          )}

          {/* NOT DETECTED */}
          {checkState === 'not_detected' && !selectingOverride && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
                <AlertTriangle size={20} className="text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-amber-300 font-medium text-sm">No external headset detected</p>
                  <p className="text-white/60 text-xs mt-1 leading-relaxed">
                    Without a headset, the backing track may bleed into your mic, which affects your
                    score. Plug in a headset and tap re-check before recording.
                  </p>
                </div>
              </div>

              {devices.length > 0 && (
                <div>
                  <p className="text-white/50 text-xs font-medium uppercase tracking-wider mb-2">
                    Detected Devices
                  </p>
                  <div className="space-y-1.5">
                    {devices.map(d => (
                      <div
                        key={d.deviceId}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-kod-purple-700/50 text-sm"
                      >
                        <Mic size={13} className="text-white/40 flex-shrink-0" />
                        <span className="text-white/80 truncate">{d.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-2">
                <button
                  onClick={handleRecheck}
                  disabled={isRechecking}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-kod-purple-700 hover:bg-kod-purple-600 text-white font-medium transition-colors disabled:opacity-60"
                >
                  <RefreshCw size={15} className={isRechecking ? 'animate-spin' : ''} />
                  {isRechecking ? 'Checking...' : 'Plug In Headset & Re-check'}
                </button>

                {devices.length > 0 && (
                  <button
                    onClick={() => setSelectingOverride(true)}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-amber-500/30 hover:border-amber-500/60 hover:bg-amber-500/10 text-amber-400/80 hover:text-amber-300 text-sm font-medium transition-colors"
                  >
                    <ChevronRight size={15} />
                    My headset is listed above — select it manually
                  </button>
                )}

                <button
                  onClick={handleContinueAnyway}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-white/10 hover:bg-white/5 text-white/40 hover:text-white/60 text-sm font-medium transition-colors"
                >
                  Continue Without Headset
                </button>
              </div>
            </div>
          )}

          {/* OVERRIDE SELECTION */}
          {checkState === 'not_detected' && selectingOverride && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 rounded-xl bg-blue-500/10 border border-blue-500/20">
                <Headphones size={20} className="text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-blue-300 font-medium text-sm">Select your headset</p>
                  <p className="text-white/60 text-xs mt-1 leading-relaxed">
                    Tap the device you're using as a headset. We'll log the name to help improve
                    detection for everyone.
                  </p>
                </div>
              </div>

              <div>
                <p className="text-white/50 text-xs font-medium uppercase tracking-wider mb-2">
                  Available Devices
                </p>
                <div className="space-y-1.5">
                  {devices.map(d => (
                    <button
                      key={d.deviceId}
                      onClick={() => handleSelectOverride(d)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg bg-kod-purple-700/50 hover:bg-kod-purple-600/60 border border-transparent hover:border-blue-500/40 text-sm transition-all group"
                    >
                      <Mic size={13} className="text-white/40 flex-shrink-0 group-hover:text-blue-400 transition-colors" />
                      <span className="text-white/80 truncate flex-1 text-left">{d.label}</span>
                      <ArrowRight size={13} className="text-white/20 group-hover:text-blue-400 flex-shrink-0 transition-colors" />
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={() => setSelectingOverride(false)}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-white/10 hover:bg-white/5 text-white/40 hover:text-white/60 text-sm font-medium transition-colors"
              >
                Back
              </button>
            </div>
          )}

          {/* PERMISSION DENIED */}
          {checkState === 'permission_denied' && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/20">
                <XCircle size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-red-300 font-medium text-sm">Microphone access denied</p>
                  <p className="text-white/60 text-xs mt-1 leading-relaxed">
                    Microphone permission is required to record. Please allow access in your browser
                    settings and try again.
                  </p>
                </div>
              </div>

              <ol className="space-y-2 text-xs text-white/50 list-decimal list-inside leading-relaxed pl-1">
                <li>Click the lock icon in your browser's address bar</li>
                <li>Set Microphone to "Allow"</li>
                <li>Reload the page if needed, then tap Try Again</li>
              </ol>

              <button
                onClick={handleRecheck}
                disabled={isRechecking}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-kod-purple-700 hover:bg-kod-purple-600 text-white font-medium transition-colors disabled:opacity-60"
              >
                <RefreshCw size={15} className={isRechecking ? 'animate-spin' : ''} />
                {isRechecking ? 'Checking...' : 'Try Again'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
